import React from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle } from 'lucide-react';
export type ServiceFormData = {
  id?: string;
  name: string;
  category: string;
  price: number;
  description: string;
  duration: string;
  warranty: string;
  isActive: boolean;
};
type ServiceFormProps = {
  defaultValues?: ServiceFormData;
  onSubmit: (data: ServiceFormData) => void;
  onCancel: () => void;
};
export function ServiceForm({
  defaultValues,
  onSubmit,
  onCancel
}: ServiceFormProps) {
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm<ServiceFormData>({
    defaultValues: defaultValues || {
      name: '',
      category: 'painting',
      price: 0,
      description: '',
      duration: '',
      warranty: '',
      isActive: true
    }
  });
  return <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-id="element-453">
      <div data-id="element-454">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-455">
          Service Name
        </label>
        <div className="mt-1 relative" data-id="element-456">
          <input id="name" type="text" className={`block w-full px-4 py-3 rounded-md border ${errors.name ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('name', {
          required: 'Service name is required'
        })} placeholder="Enter service name" data-id="element-457" />
          {errors.name && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-458">
              <AlertCircle className="h-5 w-5 text-red-500" data-id="element-459" />
            </div>}
        </div>
        {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-460">{errors.name.message}</p>}
      </div>
      <div data-id="element-461">
        <label htmlFor="category" className="block text-sm font-medium text-gray-700" data-id="element-462">
          Category
        </label>
        <div className="mt-1" data-id="element-463">
          <select id="category" className="block w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-primary focus:border-primary text-gray-900" {...register('category', {
          required: 'Category is required'
        })} data-id="element-464">
            <option value="painting" data-id="element-465">Painting</option>
            <option value="waterproofing" data-id="element-466">Waterproofing</option>
            <option value="micro-repair" data-id="element-467">Micro-repair</option>
          </select>
        </div>
        {errors.category && <p className="mt-1 text-sm text-red-600" data-id="element-468">{errors.category.message}</p>}
      </div>
      <div data-id="element-469">
        <label htmlFor="price" className="block text-sm font-medium text-gray-700" data-id="element-470">
          Price (MYR)
        </label>
        <div className="mt-1 relative" data-id="element-471">
          <input id="price" type="number" min="0" step="0.01" className={`block w-full px-4 py-3 rounded-md border ${errors.price ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('price', {
          required: 'Price is required',
          min: {
            value: 0,
            message: 'Price must be positive'
          }
        })} placeholder="Enter price" data-id="element-472" />
          {errors.price && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-473">
              <AlertCircle className="h-5 w-5 text-red-500" data-id="element-474" />
            </div>}
        </div>
        {errors.price && <p className="mt-1 text-sm text-red-600" data-id="element-475">{errors.price.message}</p>}
      </div>
      <div data-id="element-476">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-477">
          Description
        </label>
        <div className="mt-1" data-id="element-478">
          <textarea id="description" rows={4} className={`block w-full px-4 py-3 rounded-md border ${errors.description ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('description', {
          required: 'Description is required'
        })} placeholder="Enter service description" data-id="element-479" />
        </div>
        {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-480">
            {errors.description.message}
          </p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-481">
        <div data-id="element-482">
          <label htmlFor="duration" className="block text-sm font-medium text-gray-700" data-id="element-483">
            Service Duration
          </label>
          <div className="mt-1 relative" data-id="element-484">
            <input id="duration" type="text" placeholder="e.g. 2-3 days" className={`block w-full px-4 py-3 rounded-md border ${errors.duration ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('duration', {
            required: 'Duration is required'
          })} data-id="element-485" />
            {errors.duration && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-486">
                <AlertCircle className="h-5 w-5 text-red-500" data-id="element-487" />
              </div>}
          </div>
          {errors.duration && <p className="mt-1 text-sm text-red-600" data-id="element-488">
              {errors.duration.message}
            </p>}
        </div>
        <div data-id="element-489">
          <label htmlFor="warranty" className="block text-sm font-medium text-gray-700" data-id="element-490">
            Warranty Period
          </label>
          <div className="mt-1 relative" data-id="element-491">
            <input id="warranty" type="text" placeholder="e.g. 1 year" className={`block w-full px-4 py-3 rounded-md border ${errors.warranty ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('warranty', {
            required: 'Warranty period is required'
          })} data-id="element-492" />
            {errors.warranty && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-493">
                <AlertCircle className="h-5 w-5 text-red-500" data-id="element-494" />
              </div>}
          </div>
          {errors.warranty && <p className="mt-1 text-sm text-red-600" data-id="element-495">
              {errors.warranty.message}
            </p>}
        </div>
      </div>
      <div className="flex items-center" data-id="element-496">
        <input id="isActive" type="checkbox" className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded" {...register('isActive')} data-id="element-497" />
        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-498">
          Service is active and available for booking
        </label>
      </div>
      <div className="flex justify-end space-x-4" data-id="element-499">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-500">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-70" data-id="element-501">
          {isSubmitting ? 'Saving...' : defaultValues ? 'Update Service' : 'Add Service'}
        </button>
      </div>
    </form>;
}